/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useMemo
} from 'react';
import { useSelector } from 'react-redux';

import {
  ModalDialog,
  providers,
  utils
} from '@febos-io/common';
import { store } from '@febos-io/auth';

import {
  Button,
  FormikRadio,
  FormikSelect
} from '@febos-io/design';
import {
  Form,
  Formik
} from 'formik';
import documentType from '../config/documentType';
import {
  useCompanyPriceListEffect,
  useDocumentByIdEffect,
  useTranslation
} from '../hooks';
import ExamReportSkeleton from './exam/ExamReportSheleton';
import ModalPatientInformation from './ModalPatientInformation';
import ModalExamTitle from './ModalExamTitle';
import PanelInformationTextModal from './PanelInformationTextModal';
import OdontogramReport from './OdontogramReport';
import usePayCompanyOrderCallback
  from '../hooks/companyOrder/usePayCompanyOrderCallback';

export const schema = utils.validator.object().shape({
  paymentMethod: utils.validator.number().required('This field is required'),
  comprobanteType: utils.validator.string().required('This field is required')
});

export function ModalPayOrder() {
  const { t } = useTranslation();
  const {
    modalPayload: { id: orderId, createdAt, exams },
    closeModal
  } = providers.useModalContext();
  const companyId = useSelector(store.getCompanyId);

  const { loading: loadingPrices, elements: pricesArray } = useCompanyPriceListEffect(
    companyId,
    '0',
    'company',
    1,
    Number.MAX_SAFE_INTEGER
  );

  const { loading: isPaying, onPay } = usePayCompanyOrderCallback(companyId, orderId);

  const { loading, element: order } = useDocumentByIdEffect(documentType.ORDER_DENTAL, orderId);

  const isLoading = useMemo(() => loading || loadingPrices, [loading, loadingPrices]);

  const examPricesByType = useMemo(() => {
    if (loadingPrices) {
      return {};
    }
    return pricesArray.reduce((acc, item) => {
      const price = item.price || {
        elementTypeId: '0',
        value: 0
      };
      const type = price.elementTypeId;
      const amount = price.value;
      return {
        ...acc,
        [type]: amount
      };
    }, {});
  }, [loadingPrices, pricesArray]);
  const examPrices = useMemo(() => {
    if (loadingPrices || !examPricesByType) {
      return [];
    }
    return exams.map((item) => {
      return {
        ...item,
        price: examPricesByType[item.code] || 0
      };
    });
  }, [loadingPrices, exams, examPricesByType]);

  const totalPrices = useMemo(() => {
    const neto = examPrices.reduce((acc, item) => {
      return acc + item.price;
    }, 0);
    const iva = Math.round(neto * 0.19);
    const total = Math.round(neto + iva);
    return {
      neto,
      iva,
      total
    };
  }, [examPrices]);

  const isAllAmountOK = useMemo(() => {
    if (!examPrices) {
      return false;
    }
    return !examPrices.some((item) => item.price === 0);
  }, [examPrices]);
  const handleSubmit = async (values) => {
    const response = await onPay(values);
    if (response) {
      setTimeout(() => {
        closeModal({ ok: true });
      }, 1000);
    }
  };
  return (
    <ModalDialog useBackdropClose useCloseModal size="lg" title={<ModalExamTitle exam={order} />}>
      {isLoading ? (
        <ExamReportSkeleton />
      ) : (
        <div className="modal-body">
          <div className="card-body p-0">
            <ModalPatientInformation
              company={order.company}
              patient={order.patient}
              fromDate={createdAt}
            />
            <PanelInformationTextModal title={t('Medical diagnosis')} icon="user-magnifying-glass">
              <p className="rounded-2 bg-light p-1">{order.medicalDiagnosis}</p>
            </PanelInformationTextModal>
            {order.diagnostic && (
              <PanelInformationTextModal title={t('Diagnosis')} icon="user-doctor-message">
                <p className="rounded-2 bg-light p-1">{order.diagnostic}</p>
              </PanelInformationTextModal>
            )}
            {order.observations && (
              <PanelInformationTextModal title={t('Observations')} icon="comment">
                <p className="rounded-2 bg-light p-1">{order.observations}</p>
              </PanelInformationTextModal>
            )}

            {order.options && (
              <PanelInformationTextModal title={t('Options')} icon="tag">
                <div className="col">
                  {order.options.map((option) => {
                    return (
                      <span className="badge bg-primary me-1 mb-1" key={option}>
                        {option === 'Others' ? `${t(option)}: ${order.optionsOther}` : t(option)}
                      </span>
                    );
                  })}
                </div>
              </PanelInformationTextModal>
            )}
            {!!order.odontogram?.length && (
              <PanelInformationTextModal title={t('Teeth')} icon="teeth">
                <OdontogramReport
                  isChild={order.isChild}
                  isADA={order.isADA}
                  teeth={order.odontogram}
                />
              </PanelInformationTextModal>
            )}
            <div>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="py-1">{t('Exam')}</th>
                      <th className="py-1 text-end">{t('Price')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {examPrices?.map((exam) => (
                      <tr className="border-bottom" key={exam.name}>
                        <td className="py-1">
                          <p className="card-text fw-bold mb-25">{t(exam.name)}</p>
                          {/* <p className="card-text text-nowrap">{JSON.stringify(exam)}</p> */}
                        </td>
                        <td className="py-1 text-end">
                          <span className="fw-bold">
                            $
                            {exam.price}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-top-3">
                    <tr>
                      <td className="py-1 text-end">
                        <span className="fw-bold">{t('Neto')}</span>
                      </td>
                      <td className="py-1 text-end">
                        <span className="fw-bold">
                          $
                          {totalPrices.neto || 0}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-end">
                        <span className="fw-bold">{t('Iva')}</span>
                      </td>
                      <td className="py-1 text-end">
                        <span className="fw-bold">
                          $
                          {totalPrices.iva || 0}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 text-end">
                        <span className="fw-bold">{t('Total')}</span>
                      </td>
                      <td className="py-1 text-end">
                        <span className="fw-bold">
                          $
                          {totalPrices.total || 0}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
          <Formik
            initialValues={{ paymentMethod: 1, comprobanteType: '39' }}
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              isSubmitting
              /* and other goodies */
            }) => (
              <Form>
                <div className="row">
                  <div className="col-6 mb-1">
                    <label>{t('Voucher Type')}</label>
                    <div className="input-group">
                      <FormikRadio
                        name="comprobanteType"
                        label={t('Receipt')}
                        value="39"
                        group="comprobanteType"
                      />
                      <FormikRadio
                        name="comprobanteType"
                        label={t('Invoice')}
                        value="33"
                        group="comprobanteType"
                      />
                    </div>
                  </div>
                  {values.comprobanteType === '33' && (
                  <div className="col-6 mb-1">
                    <label>{`${t('Payment method')} (*)`}</label>
                    <FormikSelect
                      name="paymentMethod"
                      options={[
                        { value: 1, label: t('Cash') },
                        { value: 2, label: t('Credit') },
                        { value: 3, label: t('Free') }
                      ]}
                    />
                  </div>
                  )}

                </div>
                <div className="row text-end">
                  <Button type="submit" theme="success" disabled={isSubmitting || !isAllAmountOK} loading={isPaying}>
                    {t('Pay')}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

    </ModalDialog>
  );
}

export default ModalPayOrder;
